<template>
  <div class="manual-bid-page">
    <!-- Header -->
    <div class="page-header">
      <h2 class="page-title">Manually Add Bid</h2>
      <p class="page-subtitle">
        Select a user, pick an auction item, then place a bid on their behalf.
      </p>
    </div>

    <form class="steps-form" @submit.prevent="submitBid">
      <div class="steps-grid">
        <!-- Step 1 — User -->
        <section
          class="step-card"
          :class="{ 'is-complete': selectedUser }"
        >
          <header class="step-head">
            <span class="step-badge">1</span>
            <div class="step-titles">
              <h3 class="step-title">Select User</h3>
              <p class="step-hint">
                <template v-if="selectedUser">
                  Selected:
                  <strong>{{ selectedUser.name }}</strong>
                </template>
                <template v-else>
                  Search and tap a user below
                </template>
              </p>
            </div>
          </header>
          <div class="listbox-container">
            <input
              type="text"
              placeholder="Search users by name"
              @input="filterUsers"
              class="listbox-filter"
              inputmode="search"
              autocomplete="off"
            />
            <Listbox
              v-model="selectedUser"
              :options="filteredUsers"
              optionLabel="name"
              class="custom-listbox"
              listStyle="max-height:320px"
            >
              <template #option="slotProps">
                <div class="option-row user-row">
                  <span class="user-name">
                    {{ slotProps.option.firstName }}
                    {{ slotProps.option.lastName }}
                  </span>
                  <span
                    v-if="
                      slotProps.option.assignedTableName ||
                      slotProps.option.table
                    "
                    class="user-table"
                    :title="`Table ${
                      slotProps.option.assignedTableName ||
                      slotProps.option.table
                    }`"
                  >
                    {{
                      slotProps.option.assignedTableName ||
                      slotProps.option.table
                    }}
                  </span>
                </div>
              </template>
            </Listbox>
          </div>
        </section>

        <!-- Step 2 — Item -->
        <section
          ref="step2Ref"
          class="step-card"
          :class="{
            'is-complete': selectedItem,
            'is-disabled': !selectedUser,
          }"
        >
          <header class="step-head">
            <span class="step-badge">2</span>
            <div class="step-titles">
              <h3 class="step-title">Select Auction Item</h3>
              <p class="step-hint">
                <template v-if="selectedItem">
                  Selected:
                  <strong>
                    #{{ selectedItem.itemNumber }} — {{ selectedItem.name }}
                  </strong>
                </template>
                <template v-else-if="!selectedUser">
                  Pick a user first
                </template>
                <template v-else>
                  Search by lot number or name
                </template>
              </p>
            </div>
          </header>
          <div class="listbox-container">
            <input
              type="text"
              placeholder="Search lot number or item name"
              @input="filterAuctionItems"
              class="listbox-filter"
              inputmode="search"
              autocomplete="off"
            />
            <Listbox
              v-model="selectedItem"
              :options="filteredAuctionItems"
              optionLabel="name"
              class="custom-listbox"
              listStyle="max-height:320px"
            >
              <template #option="slotProps">
                <div class="option-row">
                  <span class="item-num">
                    #{{ slotProps.option.itemNumber }}
                  </span>
                  <span class="item-name">
                    {{ slotProps.option.name }}
                  </span>
                </div>
              </template>
            </Listbox>
          </div>

          <div v-if="currentBid" class="current-bid">
            <Message severity="info" :closable="false">
              Current Bid is: <strong>{{ currentBid }}</strong>
            </Message>
          </div>
        </section>
      </div>

      <!-- Step 3 — Actions -->
      <section
        v-if="selectedItem && selectedUser"
        ref="step3Ref"
        class="step-card step-card--actions"
      >
        <header class="step-head">
          <span class="step-badge">3</span>
          <div class="step-titles">
            <h3 class="step-title">Place Bid</h3>
            <p class="step-hint">
              Confirm the bid amount below
            </p>
          </div>
        </header>
        <BiddingActions
          v-if="!selectedItem.isDonationItem"
          :item="selectedItem"
          :user="selectedUser"
        />
        <BiddingDonationActions
          v-else
          :item="selectedItem"
          :user="selectedUser"
        />
      </section>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeMount, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";

import Message from "primevue/message";
import BiddingActions from "@/views/auction/components/BiddingActions.vue";
import BiddingDonationActions from "@/views/auction/components/BiddingActionsDonation.vue";

import Listbox from "primevue/listbox";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase"; // Ensure you have your Firebase config correctly imported

const store = useStore();

const users = ref([]);
const auctionItems = ref([]);
const filteredUsers = ref([]);
const filteredAuctionItems = ref([]);
const selectedUser = ref(null);
const selectedItem = ref(null);
const bidAmount = ref("");
const isBidAmountError = ref(false);
const isAnonymous = ref(false);
const feedbackMessage = ref("");
const feedbackType = ref(""); // "success" or "error"
const currentBid = ref(0);
const bidCounter = ref(0);

const step2Ref = ref(null);
const step3Ref = ref(null);

const scrollToStep = (el) => {
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fetchUsers = async () => {
  const querySnapshot = await getDocs(collection(db, "users"));
  users.value = querySnapshot.docs
    .map((doc) => ({
      id: doc.id,
      name: `${doc.data().firstName} ${doc.data().lastName}`,
      ...doc.data(),
    }))
    // Exclude admin users — they never need to have manual bids placed for them
    .filter((u) => u.userAdmin !== true);

  // Sort users by name A-Z
  users.value.sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  filteredUsers.value = users.value; // Initialize filteredUsers with all users
};

const fetchAuctionItems = async () => {
  const querySnapshot = await getDocs(collection(db, "auctionItems"));
  auctionItems.value = querySnapshot.docs
    .map((doc) => ({
      id: doc.id,
      itemNumber: doc.data().itemNumber, // Ensure itemNumber is fetched
      name: doc.data().name,
      ...doc.data(),
    }))
    .filter((item) => !item.isSponsorDefault);

  // Sort auction items by itemNumber (assuming itemNumber is a number)
  auctionItems.value.sort((a, b) => a.itemNumber - b.itemNumber);

  filteredAuctionItems.value = auctionItems.value; // Initialize filteredAuctionItems with all auction items
};

// Fetch the current bid for the selected auction item
const fetchCurrentBid = async (itemId) => {
  const itemDoc = await getDoc(doc(db, "auctionItems", itemId));
  const itemData = itemDoc.data();
  currentBid.value = itemData.latestBid || itemData.startbid;
  bidCounter.value = itemData.bidCount || 0;
};

// Watch for changes to the selected item and fetch the current bid
watch(selectedItem, async (newValue) => {
  if (newValue) {
    await fetchCurrentBid(newValue.id);
    await nextTick();
    scrollToStep(step3Ref.value);
  }
});

// Watch for changes to the selected user and clear the selected item
watch(selectedUser, async (newValue) => {
  selectedItem.value = null; // Clear the selected item when the selected user changes
  if (newValue) {
    await nextTick();
    scrollToStep(step2Ref.value);
  }
});

// Filter users for listbox
const filterUsers = (event) => {
  const query = event.target.value.toLowerCase();
  filteredUsers.value = users.value.filter((user) => {
    return user.name.toLowerCase().includes(query);
  });
};

// Filter auction items for listbox
const filterAuctionItems = (event) => {
  const query = event.target.value.toLowerCase();

  filteredAuctionItems.value = auctionItems.value.filter((item) => {
    const itemNumberString = item.itemNumber.toString().toLowerCase();
    const itemNameString = item.name.toLowerCase();

    return itemNumberString.includes(query) || itemNameString.includes(query);
  });
};

// Submit the bid
const submitBid = async () => {
  if (!bidAmount.value.trim() || isNaN(Number(bidAmount.value))) {
    isBidAmountError.value = true; // Set error flag if bid amount is invalid
    feedbackMessage.value = "Please enter a valid bid amount.";
    feedbackType.value = "error";
    return;
  }

  const newBidAmount = Number(bidAmount.value);
  if (newBidAmount <= currentBid.value) {
    isBidAmountError.value = true; // Set error flag if bid amount is less than or equal to the current bid
    feedbackMessage.value = `Bid amount must be higher than the current bid of ${currentBid.value}.`;
    feedbackType.value = "error";
    return;
  } else {
    isBidAmountError.value = false; // Reset error flag if validation passes
  }

  try {
    const itemRef = doc(db, "auctionItems", selectedItem.value.id);
    const bidderName = isAnonymous.value
      ? "Anonymous"
      : selectedUser.value.name;

    await addDoc(collection(itemRef, "bids"), {
      userBid: newBidAmount,
      maxBid: newBidAmount,
      comment: "Manual bid added",
      timestamp: new Date(),
      user: {
        uid: selectedUser.value.id,
        name: bidderName,
      },
    });

    await updateDoc(itemRef, {
      latestBid: newBidAmount, // Update the latestBid on the auction item
      latestBidder: bidderName, // Update the latestBidder on the auction item
      latestBidderID: selectedUser.value.id, // Update the latestBidderID on the auction item
      bidCount: bidCounter.value + 1, // Increment the bid count
    });

    console.log("Bid added for auction item ID: ", selectedItem.value.id);
    feedbackMessage.value = "Bid successfully added!";
    feedbackType.value = "success";

    // Clear all inputs
    selectedUser.value = null;
    selectedItem.value = null;
    bidAmount.value = "";
    isAnonymous.value = false;
    currentBid.value = 0;
  } catch (e) {
    console.error("Error adding bid: ", e);
    feedbackMessage.value =
      "Error adding bid. Verify that User and Auction Item has been selected. Please try again.";
    feedbackType.value = "error";
  }
};

const body = document.getElementsByTagName("body")[0];

onBeforeMount(() => {
  store.state.layout = "custom";
  store.state.showNavbar = false;
  store.state.showFooter = true;
  store.state.hideConfigButton = true;
});

onMounted(async () => {
  await fetchUsers();
  await fetchAuctionItems();
  store.state.isAbsolute = true;
});

onBeforeUnmount(() => {
  store.state.isAbsolute = false;
  store.state.layout = "default";
  store.state.showNavbar = true;
  store.state.showFooter = true;
  store.state.hideConfigButton = false;
  body.classList.remove("new-product");
});
</script>
<style scoped>
/* ── Page shell ────────────────────────────────────────────────── */
.manual-bid-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
}

.page-header {
  margin-bottom: 20px;
  padding: 18px 20px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}
.page-title {
  margin: 0 0 4px;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1b2750;
}
.page-subtitle {
  margin: 0;
  font-size: 0.95rem;
  color: #67748e;
}

/* ── Steps layout ──────────────────────────────────────────────── */
.steps-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.steps-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}
/* iPad landscape / desktop → two columns */
@media (min-width: 900px) {
  .steps-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* ── Step card ─────────────────────────────────────────────────── */
.step-card {
  background: #fff;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 2px solid transparent;
  transition: border-color 0.25s ease, opacity 0.25s ease;
  scroll-margin-top: 6rem;
}
.step-card.is-complete {
  border-color: #82d8b2;
}
.step-card.is-disabled {
  opacity: 0.55;
  pointer-events: none;
}
.step-card--actions {
  border-color: #1b2750;
}

.step-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
}
.step-badge {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #1b2750;
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
  box-shadow: 0 2px 8px rgba(27, 39, 80, 0.3);
}
.is-complete .step-badge {
  background: #2dce89;
  box-shadow: 0 2px 8px rgba(45, 206, 137, 0.35);
}
.step-titles {
  min-width: 0;
  flex: 1;
}
.step-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1b2750;
  line-height: 1.2;
}
.step-hint {
  margin: 2px 0 0;
  font-size: 0.9rem;
  color: #67748e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.step-hint strong {
  color: #1b2750;
}

/* ── Search input ──────────────────────────────────────────────── */
.listbox-container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}
.listbox-filter {
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid #e0e4eb;
  border-radius: 10px;
  font-size: 1rem;
  background: #f8f9fc;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
}
.listbox-filter:focus {
  outline: none;
  border-color: #1b2750;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 39, 80, 0.12);
}

/* ── Listbox (mobile-friendly touch targets) ───────────────────── */
.custom-listbox {
  border-radius: 10px;
  overflow: hidden;
  border: 1.5px solid #e0e4eb;
}
.custom-listbox :deep(.p-listbox-list) {
  padding: 4px;
  background: #fff;
}
.custom-listbox :deep(.p-listbox-item) {
  min-height: 48px;          /* Apple HIG recommended tap target */
  padding: 12px 14px;
  margin: 2px 0;
  cursor: pointer;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.3;
  transition: background 0.15s;
}
.custom-listbox :deep(.p-listbox-item:hover),
.custom-listbox :deep(.p-listbox-item:focus) {
  background: #f0f2f8;
}
.custom-listbox :deep(.p-listbox-item.p-highlight) {
  background: #1b2750;
  color: #fff;
}
.custom-listbox :deep(.p-listbox-item.p-highlight:hover) {
  background: #152045;
}

/* Option content */
.option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}
.item-num {
  flex-shrink: 0;
  min-width: 52px;
  padding: 2px 8px;
  background: rgba(27, 39, 80, 0.08);
  color: #1b2750;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: center;
}
.p-highlight .item-num {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}
.item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* User option with table chip */
.user-row {
  justify-content: space-between;
}
.user-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-table {
  flex-shrink: 0;
  max-width: 45%;
  padding: 3px 10px;
  background: rgba(195, 153, 53, 0.15);
  color: #8a6b17;
  border: 1px solid rgba(195, 153, 53, 0.35);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.p-highlight .user-table {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.35);
}

/* ── Current bid callout ───────────────────────────────────────── */
.current-bid {
  margin-top: 14px;
}
.current-bid :deep(.p-message) {
  border-radius: 10px;
  font-size: 1rem;
}

/* ── Mobile tweaks ─────────────────────────────────────────────── */
@media (max-width: 600px) {
  .manual-bid-page {
    padding: 10px;
  }
  .page-header,
  .step-card {
    padding: 14px;
    border-radius: 12px;
  }
  .page-title {
    font-size: 1.2rem;
  }
  .step-title {
    font-size: 1rem;
  }
  .step-badge {
    width: 36px;
    height: 36px;
    font-size: 1rem;
  }
  .listbox-filter {
    padding: 13px 14px;
    font-size: 16px; /* prevent iOS zoom on focus */
  }
  .custom-listbox :deep(.p-listbox-item) {
    min-height: 52px;   /* a bit roomier on phone */
    font-size: 1rem;
  }
}
</style>
